import { useState, useCallback, useContext } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import * as REST from '~services/rest';
import { ChatContext } from './context';

export const useConversationStarter = () => {
  const { currentUser, recipient } = useContext(ChatContext);
  const { getAccessTokenSilently } = useAuth0();

  const [variants, setVariants] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [refreshCount, setRefreshCount] = useState(0);
  const [isDismissed, setIsDismissed] = useState(false);
  const [hasFetched, setHasFetched] = useState(false);

  const fetchStarter = useCallback(async () => {
    // Idempotent: don't fetch if already fetching, or if already fetched, or if dismissed
    if (isLoading || hasFetched || isDismissed) {
      return;
    }

    setIsLoading(true);
    setHasFetched(true);

    try {
      const token = await getAccessTokenSilently();
      const payload = {
        customer_id: currentUser.accountType === 'client' ? currentUser.personId : recipient.personId,
        worker_id: currentUser.accountType === 'client' ? recipient.personId : currentUser.personId,
        sender: currentUser.accountType === 'client' ? 'customer' : 'worker',
      };

      const result = await REST.post({
        name: 'chats/conversation-starter',
        requiresAuthentication: true,
        token,
        params: payload,
        shouldThrowError: true,
      });

      if (result && result.status_msg === 'OK' && Array.isArray(result.data)) {
        setVariants(result.data);
      }
    } catch (error) {
      console.error('Error fetching conversation starter:', error);
      // Reset hasFetched on error to allow retry
      setHasFetched(false);
    } finally {
      setIsLoading(false);
    }
  }, [currentUser, recipient, getAccessTokenSilently, isLoading, hasFetched, isDismissed]);

  const refreshStarter = useCallback(async () => {
    // Allow maximum 2 variations total, meaning refreshCount should be less than 1
    // (since 1 refresh brings us to 2 variations: 1 original + 1 refreshed)
    if (isLoading || refreshCount >= 1 || isDismissed) {
      return;
    }

    setIsLoading(true);

    try {
      const token = await getAccessTokenSilently();
      const payload = {
        customer_id: currentUser.accountType === 'client' ? currentUser.personId : recipient.personId,
        worker_id: currentUser.accountType === 'client' ? recipient.personId : currentUser.personId,
        sender: currentUser.accountType === 'client' ? 'customer' : 'worker',
      };

      const result = await REST.post({
        name: 'chats/conversation-starter',
        requiresAuthentication: true,
        token,
        params: payload,
        shouldThrowError: true,
      });

      if (result && result.status_msg === 'OK' && Array.isArray(result.data)) {
        setVariants((prev) => {
          // Prevent duplicates
          const newItems = result.data.filter(
            (newItem) => !prev.some((existingItem) => existingItem.message === newItem.message)
          );
          if (newItems.length === 0) {
            return [...prev, ...result.data];
          }
          return [...prev, ...newItems];
        });
        setRefreshCount((prev) => prev + 1);
      }
    } catch (error) {
      console.error('Error refreshing conversation starter:', error);
    } finally {
      setIsLoading(false);
    }
  }, [currentUser, recipient, getAccessTokenSilently, isLoading, refreshCount, isDismissed]);

  const dismissStarter = useCallback(() => {
    setIsDismissed(true);
  }, []);

  return {
    variants,
    isLoading,
    refreshCount,
    isDismissed,
    fetchStarter,
    refreshStarter,
    dismissStarter,
  };
};
